import React from "react";

import { faCalendarMinus, faFileAlt, faPen, faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import cx from "classnames";

import { CalendarPageContext, type Event, MeetWithContext } from "~/calendar/CalendarPage";
import recurrenceChoiceModal from "~/calendar/EventForm/recurrenceChoiceModal";
import { SidePanelNavContext } from "~/calendar/SidePanel";
import { choiceModal } from "~/lib/choiceModal";
import RecordIcon, { useChangeWillBeRecordedMutation } from "~/meetingRecorder/RecordIcon";
import { Button, toast } from "~/ui";
import ContextMenu from "~/ui/contextMenu";

import ColorPicker, { type EventColor, useChangeEventColorMutation } from "../EventForm/ColorPicker";
import useDeleteEvent from "../hooks/useDeleteEvent";
import useEventAttendanceMutation from "../hooks/useEventAttendanceMutation";
import useEventVisibilityToggle from "../hooks/useEventVisibilityToggle";
import { ViewOptionsMenuTrigger } from "../Toolbar/ViewOptionsMenu";
import ConferenceLinksMenu from "./ConferenceLinksMenu";

import type { EventEditType, ResponseStatusChoices } from "~/__generated__/MeetingsPanelAttendanceMutation.graphql";

import styles from "./EventContextMenu.module.css";

interface EventContextMenuProps {
	children: React.ReactNode;
	disabled?: boolean;
	event: Event;
}

export default function EventContextMenuWrapper({ disabled, ...props }: EventContextMenuProps) {
	if (disabled) {
		return <>{props.children}</>;
	}

	return <EventContextMenu {...props}>{props.children}</EventContextMenu>;
}

function EventContextMenu({ children, disabled, event }: EventContextMenuProps) {
	const [mutationLoading, setMutationLoading] = React.useState(false);
	const { navStateDispatch } = React.useContext(SidePanelNavContext);
	const { saveCurrentMeetWithParticipants } = React.useContext(MeetWithContext);
	const eventAttendance = useEventAttendanceMutation();

	const onEditEvent = React.useCallback(() => {
		navStateDispatch({ type: "event_form", editedEvent: event });
		saveCurrentMeetWithParticipants();
	}, [event, navStateDispatch, saveCurrentMeetWithParticipants]);

	const { setEventToLoading } = React.useContext(CalendarPageContext);
	const changeEventColor = useChangeEventColorMutation(setEventToLoading);

	const undoFnMaker = React.useCallback(
		(oldColor: EventColor, editType: EventEditType) => async () => {
			const eventOrError = await changeEventColor({
				calendarId: event.calendar.id,
				guid: event.guid,
				newColor: oldColor,
				editType,
			});

			if ("name" in eventOrError) {
				toast.error(`Error changing event color: ${eventOrError.message}`);
			}

			toast.info("Undone");
		},
		[event.calendar.id, event.guid, changeEventColor],
	);

	const onSelectColor = React.useCallback(
		async (color: EventColor) => {
			const editType = event.type !== "SINGLE" ? await recurrenceChoiceModal(false, !event.isOrganizer) : "THIS";
			if (!editType) {
				return;
			}

			const eventOrError = await changeEventColor({
				calendarId: event.calendar.id,
				guid: event.guid,
				newColor: color,
				editType,
			});

			if ("name" in eventOrError) {
				toast.error(`Error changing event color: ${eventOrError.message}`);
			}

			const undoFn = undoFnMaker(event.fellowColor, editType);

			toast.success("Color changed", {
				actions: (
					<Button size="slim" variant="secondary" onClick={undoFn}>
						Undo
					</Button>
				),
			});
		},
		[changeEventColor, event, undoFnMaker],
	);

	const onHideEvent = useEventVisibilityToggle(event, {
		onHide: (
			<>
				<p style={{ pointerEvents: "none" }}>
					You can reveal hidden events by navigating to the <ViewOptionsMenuTrigger isOpen={false} /> menu at the top of
					the calendar page and selecting <i>View settings → Hidden meetings</i>.
				</p>
				<p>Hiding a recurring event will also hide all events in the series.</p>
			</>
		),
		onReveal: "Showing a recurring event will also show all events in the series.",
	});

	const onDeleteEvent = useDeleteEvent(event);

	const handleAnswerEventAttendance = React.useCallback(
		(response: ResponseStatusChoices) => async () => {
			if (mutationLoading) return;

			if (event.type !== "SINGLE" && event.calendar.source !== "office365") {
				// Then it's occurrence, need to ask for edit type
				const choices: { value: EventEditType; label: string }[] = [
					{ value: "THIS", label: "This event" },
					{ value: "ALL", label: "All events" },
				];
				const result = await choiceModal({ choices, title: "RSVP to recurring event" });
				if (result) {
					await eventAttendance({
						calendarId: event.calendar.id,
						eventId: event.id,
						eventGuid: event.guid,
						eventTitle: event.title,
						userResponseStatus: response,
						editType: result.value as EventEditType,
						setMutationLoading,
					});
				}
			} else {
				await eventAttendance({
					calendarId: event.calendar.id,
					eventId: event.id,
					eventGuid: event.guid,
					eventTitle: event.title,
					userResponseStatus: response,
					editType: "THIS",
					setMutationLoading,
				});
			}
		},
		[
			mutationLoading,
			event.type,
			event.calendar.source,
			event.calendar.id,
			event.id,
			event.guid,
			event.title,
			eventAttendance,
		],
	);

	const isTimeBlock = event.timeBlockType === "focusTime" || event.timeBlockType === "outOfOffice";

	const [changeWillBeRecorded, mutationInProgress] = useChangeWillBeRecordedMutation();
	const onChangeWillBeRecorded = React.useCallback(async () => {
		if (mutationInProgress || event.willBeRecorded === "UNKNOWN" || event.willBeRecorded === "RESTRICTED") return;

		const checked = event.willBeRecorded !== "YES";

		await changeWillBeRecorded(event.calendar.id, event.guid, checked);
	}, [event.calendar.id, event.guid, event.willBeRecorded, changeWillBeRecorded, mutationInProgress]);

	return (
		<ContextMenu>
			<ContextMenu.Trigger disabled={disabled}>{children}</ContextMenu.Trigger>
			<ContextMenu.Portal>
				<ContextMenu.Content>
					<ColorPicker
						calendarColor={event.calendarColor}
						selectedColor={event.fellowColor}
						providerEventColor={event.providerEventColor}
						onSelectColor={onSelectColor}
					/>
					<ContextMenu.Separator />
					<ContextMenu.ItemLink icon={faFileAlt} to={`/meetings/${event.guid}/`}>
						<span>Open notes</span>
					</ContextMenu.ItemLink>
					{event.willBeRecorded !== "UNKNOWN" && event.willBeRecorded !== "RESTRICTED" && (
						<ContextMenu.Item icon={<RecordIcon className={styles.recordIcon} />} onClick={onChangeWillBeRecorded}>
							<span>Turn {event.willBeRecorded === "YES" ? "off" : "on"} Auto-record</span>
						</ContextMenu.Item>
					)}
					<ConferenceLinksMenu event={event} />
					<ContextMenu.Separator />
					{event.isOrganizer && event.status !== "cancelled" && (
						<ContextMenu.Item icon={faPen} onClick={onEditEvent}>
							<span>Edit event</span>
						</ContextMenu.Item>
					)}
					<ContextMenu.Item icon={faCalendarMinus} onClick={onHideEvent}>
						<span>{event.isHidden ? "Show" : "Hide"} event in Fellow</span>
					</ContextMenu.Item>
					{event.status !== "cancelled" && event.isOrganizer && (
						<ContextMenu.Item icon={faTrashCan} destructive onClick={onDeleteEvent}>
							<span>Delete event</span>
						</ContextMenu.Item>
					)}

					{event.status !== "cancelled" && !isTimeBlock && (
						<>
							<ContextMenu.Separator />
							<div className={styles.attendanceRow}>
								<div className={styles.attendanceRowLabel}>Going?</div>

								<div className={styles.attendanceRowButtons}>
									<ContextMenu.Item
										className={cx(styles.attendanceRowButton, {
											[styles.active]: event.userResponseStatus === "accepted",
										})}
										onSelect={handleAnswerEventAttendance("accepted")}
									>
										Yes
									</ContextMenu.Item>
									<ContextMenu.Item
										className={cx(styles.attendanceRowButton, {
											[styles.active]: event.userResponseStatus === "declined",
										})}
										onSelect={handleAnswerEventAttendance("declined")}
									>
										No
									</ContextMenu.Item>
									<ContextMenu.Item
										className={cx(styles.attendanceRowButton, {
											[styles.active]:
												event.userResponseStatus === "tentative" || event.userResponseStatus === "tentativelyAccepted",
										})}
										onSelect={handleAnswerEventAttendance("tentative")}
									>
										Maybe
									</ContextMenu.Item>
								</div>
							</div>
						</>
					)}
				</ContextMenu.Content>
			</ContextMenu.Portal>
		</ContextMenu>
	);
}
